import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const RateType = {
    Contingency: "c",
    Hourly: "h",
    FlatFee: "f"
};
export type RateTypeType = typeof RateType[keyof typeof RateType];
class CaseType extends Base implements EntityType {
    caseTypeId: uuid;
    category: string;
    subcategory?: string | null;
    code?: string | null;
    description?: string | null;
    averagePrice?: number | null;
    rateType?: RateTypeType | null;
    retainer?: number | null;
    minimumTrust?: number | null;
    paidConsult?: string | null;
    constructor(doc: typeof CaseType | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            caseTypeId: genUUID(),
            category: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "caseType";
        // @ts-ignore
        this.caseTypeId = doc._caseTypeId || doc.caseTypeId || doc.casetypeid || genUUID();
        // @ts-ignore
        this.category = doc._category || doc.category || null;
        // @ts-ignore
        this.subcategory = doc._subcategory || doc.subcategory || null;
        // @ts-ignore
        this.code = doc._code || doc.code || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.averagePrice = util.coalesce(util.parseFloat(doc._averagePrice), util.parseFloat(doc.averagePrice), util.parseFloat(doc.averageprice), null);
        // @ts-ignore
        this.rateType = util.trimRight(doc._rateType) || util.trimRight(doc.rateType) || util.trimRight(doc.ratetype) || null;
        // @ts-ignore
        this.retainer = util.coalesce(util.parseFloat(doc._retainer), util.parseFloat(doc.retainer), util.parseFloat(doc.retainer), null);
        // @ts-ignore
        this.minimumTrust = util.coalesce(util.parseFloat(doc._minimumTrust), util.parseFloat(doc.minimumTrust), util.parseFloat(doc.minimumtrust), null);
        // @ts-ignore
        this.paidConsult = util.trimRight(doc._paidConsult) || util.trimRight(doc.paidConsult) || util.trimRight(doc.paidconsult) || null;
    }
    get id(): uuid { return this.caseTypeId; }
    static RATE_TYPE_VALUES = ["c", "h", "f"];
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this caseType"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.caseTypeId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): CaseType[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'caseType',
    table: 'CASE_TYPE',
    comment: 'A case type classifies legal matters handled by the firm as a category and optional subcategory (e.g. Divorce / Uncontested). It is assigned to leads and cases, and carries financial defaults — rate structure (hourly, flat fee, or contingency), required retainer, minimum trust balance, average price, and whether initial consultations are billed — used to estimate costs and guide intake for new matters.',
    fields: {
        caseTypeId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this caseType'
        },
        category: {
            type: 'string',
            required: true,
            comment: 'The category of case, e.g. divorce'
        },
        subcategory: {
            type: 'string',
            required: false,
            comment: 'subcategory, if required, e.g. uncontested'
        },
        code: {
            type: 'string',
            required: false,
            comment: 'A code to use when filing a case name, e.g. DIV for divorce'
        },
        description: {
            type: 'string',
            required: false,
            comment: 'A brief description of this case type'
        },
        averagePrice: {
            type: 'money',
            required: false,
            comment: 'The average price for this kind of case, used to estimate costs for new matters. Entered manually.'
        },
        rateType: {
            type: 'char',
            values: ['c', 'h', 'f'], // hourly, contingency or flat-fee
            valueNames: ['Contingency', 'Hourly', 'Flat Fee'],
            required: false,
            comment: 'The rate structure(s) for this matter: Hourly, Flat Fee, and/or Contingency (combinations allowed)'
        },
        retainer: {
            type: 'money',
            required: false,
            comment: 'How much retainer is required to start this case?'
        },

        // Note that minimum trust only matters if this is an hourly case.
        minimumTrust: {
            type: 'money',
            required: false,
            comment: 'What is the minimum trust amount?'
        },

        /**
         * We need a boolean with a null possible, so the values here are t, f and null.
         */
        paidConsult: {
            type: 'char', comment: 'Should initial consults for this case type be charged for?'
        }
    }
});
    }
}
export default CaseType;
